import {DeleteRecordsConfirmationButton,} from '@flexinet/ui-components';
import {useQueryClient} from '@tanstack/react-query';

import {pipelinesKeys, useDeletePipelineMutation} from '../hooks/usePipelinesQuery';
import {usePipelinesSelection} from "../hooks/usePipelinesSelection.ts";


export const DeleteRecordButton = () => {

    const deletePipelineMutation = useDeletePipelineMutation();
    const queryClient = useQueryClient();

    const {
        selection,
        handleClearSelection,
    } = usePipelinesSelection();

    const recordIds = Object.keys(selection).filter(id => selection[id] === true);
    const disabled = recordIds.length === 0;

    // FNP-1576: ONE REJECTION MUST NOT ABANDON THE REST OF THE SELECTION.
    //
    // This awaited `mutateAsync(id)` in a bare loop with no catch, so the first
    // rejection threw straight out: every later record was never attempted, and
    // `handleClearSelection()` below never ran — leaving rows checked that may
    // no longer exist. From the operator's side a multi-delete "half worked",
    // with nothing saying which half.
    //
    // ⚠️ SEQUENTIAL, DELIBERATELY. `Promise.all` would fire N concurrent
    // deletes and N invalidations against a list the operator is watching.
    // These are a handful of records chosen by hand.
    const handleDelete = async () => {
        for (const id of recordIds) {
            try {
                await deletePipelineMutation.mutateAsync(id);
            } catch {
                // Already surfaced by the mutation's onError, and the list is
                // refreshed by its onSettled. Swallowed here only so the
                // remaining records are still attempted.
            }
        }
        // Reached on every path now.
        handleClearSelection();

        // ⚠️ THIS IS THE ACTUAL FIX FOR THE REPORTED DEFECT. ONE FINAL REFETCH,
        // AFTER THE WHOLE LOOP.
        //
        // The per-mutation invalidation was never missing here and the query key
        // was never wrong — `Reload` calls `invalidateQueries(pipelinesKeys.all)`,
        // the exact same call the mutation already made, and Reload worked.
        // Only the TIMING differed.
        //
        // Each mutation's own invalidation fires as it finishes, so the refetch
        // triggered by record N resolves WHILE record N+1 is still in flight —
        // and it returns a list that still contains N+1. With a 3-row delete
        // every refetch lands mid-loop and the table keeps all three rows.
        //
        // Reproduced in dev before the fix: three rows selected, three "Pipeline
        // deleted" toasts, NO GraphQL error, all three rows still on screen —
        // and the table had gone from 13 records to 10. A manual Reload, the
        // same call one moment later, emptied them correctly.
        //
        // Invalidating once more here, after every delete has settled, is the
        // only read guaranteed to happen after the last write.
        await queryClient.invalidateQueries({queryKey: pipelinesKeys.all});
    };

    return (<DeleteRecordsConfirmationButton
        disabled={disabled}
        recordIdsLength={recordIds.length}
        handleConfirm={handleDelete}
    />);

};
